/**
 * Archive hook status events.
 *
 * Published by the server (via the `workspace.archiveHookStatus` WS channel)
 * while executing the `onThreadArchive.commands` defined in a project's
 * `.taskin/config.json`. The frontend drives a toast + read-only output modal
 * off these events.
 *
 * Commands run sequentially. On first non-zero exit or timeout, the hook
 * aborts with `phase: "error"` and `failedStepIndex`.
 */
import { Schema } from "effect";
import { IsoDateTime, NonNegativeInt, ThreadId, TrimmedNonEmptyString } from "./baseSchemas";

export const ArchiveHookPhaseSchema = Schema.Literals([
  "started",
  "step-started",
  "step-finished",
  "completed",
  "error",
]);
export type ArchiveHookPhase = typeof ArchiveHookPhaseSchema.Type;

const ArchiveHookOutputSchema = Schema.String.check(Schema.isMaxLength(1_048_576));

export const ArchiveHookStepResultSchema = Schema.Struct({
  stepIndex: NonNegativeInt,
  command: Schema.String.check(Schema.isMaxLength(4_096)),
  exitCode: Schema.NullOr(Schema.Int),
  stdout: ArchiveHookOutputSchema,
  stderr: ArchiveHookOutputSchema,
  timedOut: Schema.Boolean,
  stdoutTruncated: Schema.Boolean,
  stderrTruncated: Schema.Boolean,
  durationMs: NonNegativeInt,
});
export type ArchiveHookStepResult = typeof ArchiveHookStepResultSchema.Type;

export const ArchiveHookEventSchema = Schema.Struct({
  threadId: ThreadId,
  projectId: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  totalSteps: NonNegativeInt,
  phase: ArchiveHookPhaseSchema,
  stepIndex: Schema.optional(NonNegativeInt),
  command: Schema.optional(Schema.String.check(Schema.isMaxLength(4_096))),
  result: Schema.optional(ArchiveHookStepResultSchema),
  failedStepIndex: Schema.optional(NonNegativeInt),
  startedAt: IsoDateTime,
  occurredAt: IsoDateTime,
});
export type ArchiveHookEvent = typeof ArchiveHookEventSchema.Type;
